import './index.scss';
import React, {lazy, useCallback, useMemo, useRef} from "react";
import ElementIds from "../../core/models/element-ids";
import LoadingBar, {LoadingBarRef} from "react-top-loading-bar";
import {FadeAnimationLayout, LoadingBarTrigger, LoadingBarTriggerContextValue, LoadingBarTriggerProvider} from '../../@bizkeytech';
import AppRoutes from "../../core/models/routes";
import {matchRoutes, Navigate, Route, Routes, useLocation} from "react-router-dom";

const ErrorViews = lazy(() => import("./public/error"));
const NotFoundErrorView = lazy(() => import("./public/error/not-found"));
const AccessDeniedErrorView = lazy(() => import("./public/error/access-denied"));
const ServerErrorErrorView = lazy(() => import("./public/error/server-error"));

const PublicViews = lazy(() => import("./public"));
const LandingView = lazy(() => import("./public/landing"));
const SpireView = lazy(() => import("./public/spire"));
const PackageView = lazy(() => import("./public/package"));
const BlogsView = lazy(() => import("./public/blogs"));
const BlogView = lazy(() => import("./public/blog"));
const PrivacyPolicyView = lazy(() => import("./public/privacy-policy"));

const ApplicationViews = () => {
    const location = useLocation();
    const loadingBarRef = useRef<LoadingBarRef | null>(null);

    /**
     * Starts the animation of the LoadingBar component.
     */
    const onRoutesLoadingStarted = useCallback<LoadingBarTriggerContextValue['onStart']>(() => {
        loadingBarRef.current?.continuousStart?.(0, 300);
    }, [loadingBarRef])

    /**
     * Completes the animation of the LoadingBar component.
     */
    const onRoutesLoadingFinished = useCallback<LoadingBarTriggerContextValue['onFinish']>(() => {
        loadingBarRef.current?.complete?.();
    }, [loadingBarRef])

    const motionKey = useMemo<string>(() => {
        const nonPanelRoutes: string[] = [
            AppRoutes.public.error._base,
        ];
        if (!!matchRoutes(
            nonPanelRoutes.map(e => ({path: e, end: false})),
            location.pathname
        )) {
            return location.pathname
        }
        return "app";

    }, [location.pathname])

    return (
        <LoadingBarTriggerProvider value={{onStart: onRoutesLoadingStarted, onFinish: onRoutesLoadingFinished}}>
            <div className={'application-view'}>
                <FadeAnimationLayout motionKey={motionKey} id={ElementIds.appId} className={'app'}>
                    <LoadingBar
                        ref={loadingBarRef}
                        containerClassName={'loading-bar-container'}
                        className={'loading-bar'}
                        color={'transparent'}
                    />
                    <LoadingBarTrigger>
                        <Routes>
                            <Route element={<PublicViews/>}>
                                <Route index element={<LandingView/>}/>
                                <Route path={AppRoutes.public._relative.spire} element={<SpireView/>}/>
                                <Route path={AppRoutes.public._relative.package} element={<PackageView/>}/>
                                <Route path={AppRoutes.public._relative.blogs} element={<BlogsView/>}/>
                                <Route path={AppRoutes.public._relative.blog} element={<BlogView/>}/>
                                <Route path={AppRoutes.public._relative.privacyPolicy} element={<PrivacyPolicyView/>}/>
                            </Route>

                            {/* --- Error Views ---*/}
                            <Route path={AppRoutes.public.error.base} element={<ErrorViews/>}>
                                <Route path={AppRoutes.public.error._relative.notFound} element={<NotFoundErrorView/>}/>
                                <Route path={AppRoutes.public.error._relative.accessDenied} element={<AccessDeniedErrorView/>}/>
                                <Route path={AppRoutes.public.error._relative.serverError} element={<ServerErrorErrorView/>}/>
                                <Route index element={<Navigate to={AppRoutes.public.error.notFound} replace/>}/>
                                <Route path="*" element={<Navigate to={AppRoutes.public.error.notFound} replace/>}/>
                            </Route>

                            {/* --- Fallback Route ---*/}
                            <Route path="*" element={<Navigate to={AppRoutes.public.error.base} replace/>}/>
                        </Routes>
                    </LoadingBarTrigger>
                </FadeAnimationLayout>
            </div>
        </LoadingBarTriggerProvider>
    );
}

export default ApplicationViews;
